import { useMemo } from 'react';
import type { UiLabelKey } from '@canvas-logic/clp-api-types';
import { useDisplayContext, useTranslator, type RequestQuoteOverrides } from '@canvas-logic/clp-shared-ui';

/**
 * `RequestQuoteOverrides` property → `ui.requestQuote.*` canonical.
 *
 * The contact form's framework strings (required hint, validation copy, step
 * and submit buttons, consent checkbox) are rendered by clp-shared-ui
 * primitives that read the package's own bundled i18next catalog. That catalog
 * ships only `en` and `de` and is never exported to the project localization
 * CSV, so a project could fill out its CSV completely and still see these
 * strings in English. Declaring them as canonicals puts them in the CSV;
 * resolving them here puts the CSV value on screen.
 *
 * Names follow the approved design spec
 * `docs/superpowers/specs/2026-07-15-contact-form-project-translatable-strings-design.md`.
 */
const OVERRIDE_CANONICALS: Record<keyof RequestQuoteOverrides, UiLabelKey> = {
  nextButton: 'ui.requestQuote.nextButton',
  sendButton: 'ui.requestQuote.sendButton',
  requiredHint: 'ui.requestQuote.requiredHint',
  validationRequired: 'ui.requestQuote.validation.required',
  validationEmail: 'ui.requestQuote.validation.email',
  acceptText: 'ui.requestQuote.acceptConditions.text',
  acceptValidation: 'ui.requestQuote.acceptConditions.validation',
};

/**
 * Resolve the contact form's framework strings through the UI cascade
 * (ADR-0045), returning only entries whose value came from a PAYLOAD layer.
 *
 * The `registry` layer is deliberately excluded: it means the project has no
 * translation for that canonical, and the registry's English system value is
 * NOT what we want on screen — omitting the key lets `RequestQuoteForm` fall
 * back to its bundled i18next catalog, which at least honours the active
 * language for `en`/`de`. Same contract as `useContactFormFieldLabels`.
 *
 * Unlike `useContactFormFieldLabels`, these canonicals are fixed rather than
 * schema-derived, so the hook takes no `schema` argument.
 */
export function useContactFormOverrides(): RequestQuoteOverrides {
  const translator = useTranslator();
  const ctx = useDisplayContext();

  return useMemo(() => {
    const out: RequestQuoteOverrides = {};

    for (const [prop, canonical] of Object.entries(OVERRIDE_CANONICALS) as [
      keyof RequestQuoteOverrides,
      UiLabelKey,
    ][]) {
      const hit = translator.uiWithSource(canonical, ctx);
      if (hit.layer === 'selected' || hit.layer === 'default' || hit.layer === 'system') {
        out[prop] = hit.value;
      }
    }

    return out;
  }, [translator, ctx]);
}
